import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient } from '@src/api/apiClient/axiosClient';

import { Pagination } from '../../types/pagination';
import { SupportService, SupportServiceForm } from '../../types/supportService';

type SupportServiceIndexRequest = {
  keyword?: string;
  support_service_category_id?: number;
  support_service_tag_id?: number;
  status?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  direction?: string;
};

type SupportServiceIndexResponse = {
  data: SupportService[];
  pagination: Pagination;
};
type SupportServiceBulkUpdateResponse = unknown;
type SupportServiceBulkUpdateErrorResponse = unknown;

type SupportServiceBulkUpdateRequest = {
  support_service_ids: number[];
  support_service_category_id: number;
};

type SupportServiceShowResponse = SupportService;
interface SupportServiceFormRequest extends SupportServiceForm {
  status: number;
}

type SupportServiceCreateResponse = {
  published_at: string;
  tenant_user_id: number;
  support_service_category_id: number;
  title: string;
  description: string;
  thumbnail_url: string;
  status: number;
  updated_at: string;
  created_at: string;
  id: number;
};

type SupportServiceFormErrors = {
  [key: string]: string[];
  title: string[];
  published_at: string[];
  support_service_category_id: string[];
  support_service_tags: string[];
  description: string[];
  thumbnail: string[];
  content: string[];
};

type SupportServiceUpdateResponse = SupportServiceCreateResponse;

type SupportServicePreviewResponse = {
  id: number;
  support_service_id: number;
  uuid: string;
};
type SupportServicePreviewErrorResponse = SupportServiceFormErrors;

type SupportServiceUploadFileResponse = {
  url: string;
};

type SupportServiceUploadFileErrorResponse = {
  file: string[];
};

type SupportServiceDeleteFileResponse = unknown;
type SupportServiceDeleteFileErrorResponse = {
  message: string;
};

export const supportServiceApiClient = {
  index: async (
    params: SupportServiceIndexRequest = {},
  ): Promise<ApiResult<SupportServiceIndexResponse>> => {
    return await axiosClient.get('/tenant/support-services', { params });
  },
  show: async (id: number): Promise<ApiResult<SupportServiceShowResponse>> => {
    return await axiosClient.get(`/tenant/support-services/${id}`);
  },
  findPreviewBySupportServicePreviewUuid: async (
    uuid: string,
  ): Promise<ApiResult<SupportServiceShowResponse>> => {
    return await axiosClient.get(`/tenant/support-services/preview/${uuid}`);
  },
  create: async (
    params: Partial<SupportServiceFormRequest>,
  ): Promise<ApiResult<SupportServiceCreateResponse, SupportServiceFormErrors>> => {
    return await axiosClient.post('/tenant/support-services', params);
  },
  update: async (
    id: number,
    params: Partial<SupportServiceFormRequest>,
  ): Promise<ApiResult<SupportServiceUpdateResponse, SupportServiceFormErrors>> => {
    return await axiosClient.put(`/tenant/support-services/${id}`, params);
  },
  preview: async (
    id: number,
    params: Partial<SupportServiceFormRequest>,
  ): Promise<ApiResult<SupportServicePreviewResponse, SupportServicePreviewErrorResponse>> => {
    return await axiosClient.post(`/tenant/support-services/${id}/preview`, params);
  },
  bulkUpdateCategory: async (
    params: SupportServiceBulkUpdateRequest,
  ): Promise<
    ApiResult<SupportServiceBulkUpdateResponse, SupportServiceBulkUpdateErrorResponse>
  > => {
    return await axiosClient.put('/tenant/support-services/category', params);
  },
  delete: async (id: number): Promise<ApiResult> => {
    return await axiosClient.delete(`/tenant/support-services/${id}`);
  },
  uploadFile: async (
    file: File,
  ): Promise<
    ApiResult<SupportServiceUploadFileResponse, SupportServiceUploadFileErrorResponse>
  > => {
    const formData = new FormData();
    formData.append('file', file);
    return await axiosClient.post('/tenant/support-services/upload', formData);
  },
  deleteFile: async (
    url: string,
  ): Promise<
    ApiResult<SupportServiceDeleteFileResponse, SupportServiceDeleteFileErrorResponse>
  > => {
    return await axiosClient.delete('/tenant/support-services/s3delete', {
      params: {
        uploaded_image_url: url,
      },
    });
  },
};
